import type { TBranding } from '@fe-core/api';
import Color from 'color';
import { useEffect } from 'react';

type ThemeName = 'bx' | 'bx2023' | undefined;

const setProperties = (properties: Record<string, string>) => {
  if (!document.documentElement) return;

  const root = document.documentElement;

  Object.keys(properties).forEach((key) => {
    root.style.setProperty(key, properties[key]);
  });
};

const getFontFamily = (branding: TBranding, themeName?: ThemeName) => {
  switch (themeName) {
    case 'bx':
      return ['Open Sans'];
    case 'bx2023':
      return ['Noto sans'];

    default:
      return [
        ...(branding.extradefaultfont ? [branding.extradefaultfont] : []),
        'new-hero',
      ];
  }
};

type TColorVariants = 'dark' | 'default' | 'light';
const tintVariantsV2: Record<TColorVariants, number> = {
  light: 0.32,
  default: 0.0,
  dark: 0.32,
};

const getVariants = ({
  color,
  cssVariablePrefix,
}: {
  color: string;
  cssVariablePrefix: string;
}): Record<string, string> => {
  const c = Color(color);

  return {
    [`${cssVariablePrefix}`]: c.hex(),
    [`${cssVariablePrefix}-contrast`]: c.isLight() ? 'black' : 'white',
    [`${cssVariablePrefix}-dark`]: c.darken(tintVariantsV2.dark).hex(),
    [`${cssVariablePrefix}-dark-contrast`]: c
      .darken(tintVariantsV2.dark)
      .isLight()
      ? 'black'
      : 'white',
    [`${cssVariablePrefix}-light`]: c.lighten(tintVariantsV2.light).hex(),
    [`${cssVariablePrefix}-light-contrast`]: c
      .lighten(tintVariantsV2.light)
      .isLight()
      ? 'black'
      : 'white',
  };
};

const applyBranding = (brandingData: TBranding, themeName: ThemeName) => {
  const fontFamily = getFontFamily(brandingData, themeName)
    .join(', ')
    .replace(/,+$/, '');

  const primaryColors = getVariants({
    color: brandingData.cpPrimaryColor,
    cssVariablePrefix: '--theme-colors-primary',
  });

  const secondaryColors = getVariants({
    color: brandingData.cpSecondaryColor,
    cssVariablePrefix: '--theme-colors-secondary',
  });

  const navColors = getVariants({
    color: brandingData.cpNavColor,
    cssVariablePrefix: '--theme-colors-nav',
  });

  setProperties({
    ...primaryColors,
    ...secondaryColors,
    ...navColors,
    ...(brandingData.cpLogoNavBg && {
      '--core-header-logo-bg': 'var(--theme-colors-nav)',
    }),
    '--theme-font-family': fontFamily,
  });
};

export const useApplyBranding = (
  branding?: TBranding | null,
  theme?: string,
) => {
  useEffect(() => {
    if (branding) {
      applyBranding(branding, theme as ThemeName);
    }
  }, [theme, branding]);
};
